import { Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { catchError, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertsService } from '../../services/alerts.service';
import { Alert } from '../../models/alerts';
import { AlertPreviewStackComponent } from '../../../../../../components/alerts/components/alert-preview-stack/alert-preview-stack.component';

@Component({
  selector: 'app-alert-preview',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatButton,
    AlertPreviewStackComponent
  ],
  templateUrl: './alert-preview.component.html',
  styleUrl: './alert-preview.component.scss'
})
export class AlertPreviewComponent implements OnInit {
  liveAlerts: Alert[] = [];
  loading = true;
  hasError = false;

  // Lets AlertsComponent's own liveCount badge/mobile description reuse this dialog's single
  // getLive() fetch instead of AlertsComponent issuing its own separate one on every preview
  // open — see AlertsComponent.openPreview().
  @Output() alertsLoaded = new EventEmitter<Alert[]>();

  // Purely a client-side simulation of session dismissal (the real per-user session-dismiss
  // store is Phase 2, not built yet) — scoped to this dialog instance only, per the spec's
  // "Admin Preview... simulation" wording. Never persisted, never sent to the backend.
  private dismissedIds = new Set<number>();

  private alertsService = inject(AlertsService);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.load();
  }

  get visibleAlerts(): Alert[] {
    return this.liveAlerts.filter(alert => !this.dismissedIds.has(alert.id));
  }

  dismiss(id: number) {
    this.dismissedIds.add(id);
  }

  private load() {
    this.loading = true;
    this.hasError = false;
    this.alertsService
      .getLive()
      .pipe(
        tap(alerts => {
          this.liveAlerts = alerts;
          this.dismissedIds.clear();
          this.loading = false;
          this.alertsLoaded.emit(alerts);
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.hasError = true;
          this.loading = false;
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
